import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, ShieldAlert, X, Fingerprint } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BOOT_LINES = [
  "REENTRY STAFFING // SECURE COMMAND UPLINK",
  "INITIALIZING ENCRYPTED CHANNEL......... OK",
  "ROUTING THROUGH NAVAL RELAY [ SAT-7 ]... OK",
  "BIOMETRIC LAYER: BYPASSED",
  "AUTHORIZATION LEVEL REQUIRED: OMEGA",
  "ENTER COMMAND CIPHER TO PROCEED_",
];

function MatrixRain() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    const chars = "アイウエオカキクケコサシスセソタチツテトﾊﾋﾌﾍﾎ0123456789:.=*+-<>¦".split("");
    const font = 16;
    let cols = Math.floor(w / font);
    let drops = Array(cols).fill(1);
    let raf;
    const draw = () => {
      ctx.fillStyle = "rgba(6,10,8,0.09)";
      ctx.fillRect(0, 0, w, h);
      ctx.font = `${font}px 'JetBrains Mono', monospace`;
      for (let i = 0; i < drops.length; i++) {
        const t = chars[Math.floor(Math.random() * chars.length)];
        const x = i * font;
        const y = drops[i] * font;
        ctx.fillStyle = Math.random() > 0.975 ? "#eafff2" : "#19c37d";
        ctx.fillText(t, x, y);
        if (y > h && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      cols = Math.floor(w / font);
      drops = Array(cols).fill(1);
    };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 opacity-70" />;
}

export default function SecretAccess({ open, onClose }) {
  const { secretAccess, refresh } = useAuth();
  const [lines, setLines] = useState([]);
  const [phase, setPhase] = useState("boot"); // boot | input | granted | denied
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  // Boot typing sequence
  useEffect(() => {
    if (!open) return;
    setLines([]); setPhase("boot"); setCode("");
    let li = 0, ci = 0; let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (li >= BOOT_LINES.length) { setPhase("input"); setTimeout(() => inputRef.current?.focus(), 100); return; }
      const full = BOOT_LINES[li];
      ci++;
      setLines((prev) => { const cp = [...prev]; cp[li] = full.slice(0, ci); return cp; });
      if (ci >= full.length) { li++; ci = 0; setTimeout(tick, 90); }
      else setTimeout(tick, 9 + Math.random() * 12);
    };
    const start = setTimeout(tick, 250);
    return () => { cancelled = true; clearTimeout(start); };
  }, [open]);

  const escClose = useCallback((e) => { if (e.key === "Escape" && phase !== "granted") onClose?.(); }, [onClose, phase]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", escClose);
    return () => window.removeEventListener("keydown", escClose);
  }, [open, escClose]);

  const authenticate = async () => {
    if (!code || busy) return;
    setBusy(true);
    try {
      await secretAccess(code);
      setPhase("granted");
      setTimeout(async () => { await refresh(); /* app flips to admin dashboard */ }, 2100);
    } catch {
      setPhase("denied");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => { setPhase("input"); setCode(""); inputRef.current?.focus(); }, 1400);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="secret-access-overlay"
          className="fixed inset-0 z-[200] bg-[#040806] grid place-items-center px-4 overflow-hidden font-mono-tech"
        >
          <MatrixRain />
          <div className="secret-scanlines absolute inset-0 pointer-events-none" />

          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            className={`relative w-full max-w-lg bg-[#03130c]/90 backdrop-blur-md border ${phase === "denied" ? "border-[#ff2d2d]" : "border-[#19c37d]/60"} shadow-[0_0_60px_rgba(25,195,125,0.35)] ${shake ? "sa-shake" : ""}`}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b ${phase === "denied" ? "border-[#ff2d2d]/50" : "border-[#19c37d]/40"}`}>
              <div className="flex items-center gap-2 text-[#19c37d]">
                <Fingerprint size={16} />
                <span className="text-[11px] tracking-[0.25em] uppercase">CLASSIFIED // OMEGA CLEARANCE</span>
              </div>
              {phase !== "granted" && <button data-testid="secret-close" onClick={onClose} className="text-[#19c37d]/60 hover:text-[#19c37d]"><X size={18} /></button>}
            </div>

            <div className="p-6">
              {/* Boot log */}
              <div className="space-y-1 text-[13px] leading-relaxed text-[#19c37d] min-h-[150px]">
                {lines.map((l, i) => (
                  <div key={i} className="whitespace-pre-wrap">
                    <span className="text-[#0e8a58]">&gt; </span>{l}
                  </div>
                ))}
              </div>

              {phase === "input" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
                  <div className="flex items-center gap-2 text-[#19c37d] text-[10px] tracking-[0.2em] uppercase mb-2"><Lock size={12} /> COMMAND CIPHER</div>
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      data-testid="secret-passcode-input"
                      type="password"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") authenticate(); }}
                      placeholder="••••••••••"
                      className="flex-1 bg-[#020a06] border border-[#19c37d]/40 focus:border-[#19c37d] outline-none text-[#eafff2] tracking-[0.3em] px-4 py-3 placeholder-[#0e8a58]/50 caret-[#19c37d]"
                    />
                    <button
                      data-testid="secret-authenticate"
                      onClick={authenticate}
                      disabled={busy}
                      className="bg-[#19c37d] hover:bg-[#22e08e] text-[#03130c] font-bold uppercase tracking-[0.15em] text-xs px-5 transition-colors"
                    >
                      {busy ? "···" : "Enter"}
                    </button>
                  </div>
                  <div className="mt-3 text-[9px] tracking-[0.2em] text-[#0e8a58] uppercase animate-pulse">AWAITING AUTHORIZATION · UNAUTHORIZED ACCESS IS LOGGED</div>
                </motion.div>
              )}

              {phase === "denied" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="secret-denied"
                  className="mt-4 flex items-center gap-3 text-[#ff2d2d]">
                  <ShieldAlert size={22} />
                  <div>
                    <div className="font-bold tracking-[0.2em] text-lg glitch" data-text="ACCESS DENIED">ACCESS DENIED</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff6b6b]">INVALID CIPHER · RE-ATTEMPT AUTHORIZED</div>
                  </div>
                </motion.div>
              )}

              {phase === "granted" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="secret-granted" className="mt-4">
                  <div className="text-[#19c37d] font-bold tracking-[0.25em] text-2xl glitch" data-text="ACCESS GRANTED">ACCESS GRANTED</div>
                  <div className="mt-3 space-y-1 text-[12px] text-[#19c37d]">
                    <div>&gt; IDENTITY CONFIRMED — CLEARANCE: OMEGA</div>
                    <div>&gt; DECRYPTING COMMAND DECK...</div>
                  </div>
                  <div className="mt-3 h-1.5 bg-[#0a2016] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#19c37d] to-[#eafff2] sa-progress" />
                  </div>
                  <div className="mt-2 text-[9px] tracking-[0.2em] text-[#0e8a58] uppercase">LOADING REENTRY CONTROL DECK</div>
                </motion.div>
              )}
            </div>
          </motion.div>

          <style>{`
            .secret-scanlines { background: repeating-linear-gradient(to bottom, rgba(25,195,125,0.05) 0px, rgba(25,195,125,0.05) 1px, transparent 2px, transparent 4px); mix-blend-mode: screen; }
            .sa-shake { animation: saShake 0.45s cubic-bezier(.36,.07,.19,.97); }
            @keyframes saShake { 10%,90%{transform:translateX(-2px)} 20%,80%{transform:translateX(4px)} 30%,50%,70%{transform:translateX(-8px)} 40%,60%{transform:translateX(8px)} }
            .sa-progress { width:0%; animation: saProg 2s ease-out forwards; }
            @keyframes saProg { to { width:100%; } }
            .glitch { position:relative; }
            .glitch::before, .glitch::after { content: attr(data-text); position:absolute; left:0; top:0; width:100%; overflow:hidden; }
            .glitch::before { color:#00fff0; transform:translate(-2px,0); clip-path: inset(0 0 55% 0); animation: glitchTop 1.6s infinite linear alternate; }
            .glitch::after { color:#ff00c8; transform:translate(2px,0); clip-path: inset(55% 0 0 0); animation: glitchBot 1.4s infinite linear alternate; }
            @keyframes glitchTop { 0%{transform:translate(-2px,0)} 50%{transform:translate(2px,-1px)} 100%{transform:translate(-1px,1px)} }
            @keyframes glitchBot { 0%{transform:translate(2px,0)} 50%{transform:translate(-2px,1px)} 100%{transform:translate(1px,-1px)} }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
